import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import HomeSection from "../../components/store/HomeSection";
import CampaignsRail from "../../components/store/CampaignsRail";
import SocialRail from "../../components/store/SocialRail";
import {
  useBranding, resolveBrandImage, resolveAppDisplayName,
} from "../../context/BrandingContext";
import {
  KalkiHero, KalkiCategoryGrid,
  MyntraHero, MyntraCategoryGrid,
  AjioHero, AjioCategoryGrid,
  MangoHero, MangoCategoryGrid,
  BibaHero, BibaCategoryGrid,
  NeerusHero, NeerusCategoryGrid,
  FlipkartHero, FlipkartCategoryGrid,
  AmazonHero, AmazonCategoryGrid,
  TrendingGrid,
} from "./home/heroVariants";
import { AsFashionHero, AsFashionCategoryGrid } from "./home/AsFashionHero";
import AsFashionHome from "./AsFashionHome";

const HERO_BY_PRESET = {
  kalki: KalkiHero,
  myntra: MyntraHero,
  ajio: AjioHero,
  mango: MangoHero,
  biba: BibaHero,
  neerus: NeerusHero,
  flipkart: FlipkartHero,
  amazon: AmazonHero,
  asfashion: AsFashionHero,
};
const CATEGORY_BY_PRESET = {
  kalki: KalkiCategoryGrid,
  myntra: MyntraCategoryGrid,
  ajio: AjioCategoryGrid,
  mango: MangoCategoryGrid,
  biba: BibaCategoryGrid,
  neerus: NeerusCategoryGrid,
  flipkart: FlipkartCategoryGrid,
  amazon: AmazonCategoryGrid,
  asfashion: AsFashionCategoryGrid,
};

// No hardcoded stock images. These two Unsplash URLs rendered on every
// storefront that had not configured the blocks, putting one vertical's
// marketing on unrelated tenants. Fall back to the tenant's own logo, or
// render no image at all.

const FEATURED_DEFAULTS = {
  overline: "Editor's Pick",
  title: "Mega Deals across every department",
  body: "Save up to 70% on smartphones, laptops, kitchen appliances and beauty. Genuine products, fast shipping, and easy 7-day returns — every single day.",
  cta_label: "Shop the Deals",
  cta_link: "/shop?sort=discount",
};

// Deterministic accent colour from brand slug (for the fallback tile).
const BRAND_PALETTE = [
  "#0F3D91", "#7c3a4b", "#1428A0", "#0066A1", "#A50034",
  "#005EAA", "#E61E2A", "#FC2779", "#E8B14B", "#005EB8",
];
function brandColor(b) {
  if (b?.accent) return b.accent;
  const key = b?.slug || b?.name || "";
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) | 0;
  return BRAND_PALETTE[Math.abs(hash) % BRAND_PALETTE.length];
}

function BrandTile({ brand: b }) {
  const [imgOk, setImgOk] = React.useState(!!b.logo_url);
  const color = brandColor(b);
  return (
    <Link
      to={`/shop?brand=${encodeURIComponent(b.slug)}`}
      className="group aspect-square bg-white border border-[hsl(var(--kb-border))] rounded-sm flex items-center justify-center p-4 hover:border-[hsl(var(--kb-primary))] hover:shadow-sm transition-all overflow-hidden"
      data-testid={`brand-tile-${b.slug}`}
    >
      {imgOk && b.logo_url ? (
        <img
          src={b.logo_url}
          alt={b.name}
          className="max-h-12 max-w-full object-contain group-hover:scale-105 transition-transform"
          loading="lazy"
          onError={() => setImgOk(false)}
        />
      ) : (
        <div
          className="w-full h-full flex flex-col items-center justify-center text-white rounded-sm"
          style={{
            background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
          }}
        >
          <div className="font-display text-lg leading-tight text-center px-2 break-words">
            {b.name}
          </div>
          {(b.category_slugs || []).length > 0 && (
            <div className="text-[10px] uppercase tracking-widest text-white/70 mt-1">
              {(b.category_slugs || [])[0]?.replace(/-/g, " ")}
            </div>
          )}
        </div>
      )}
    </Link>
  );
}

function BrandsRail({ brands }) {
  if (!brands || brands.length === 0) return null;
  return (
    <section className="px-6 md:px-12 lg:px-24 py-12 bg-white" data-testid="home-brands-rail">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline mb-2">Shop by Brand</div>
          <h2 className="font-display text-2xl md:text-3xl">Top Brands</h2>
        </div>
        <Link to="/shop" className="text-sm uppercase tracking-wider text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))]">
          View all →
        </Link>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
        {brands.slice(0, 16).map((b) => (
          <BrandTile key={b.id} brand={b} />
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const { branding } = useBranding();
  const preset = branding?.theme_preset || "kalki";

  // AS Fashion has a bespoke home layout that mirrors asfashion.store —
  // completely different from the shared composition. Short-circuit so
  // other presets are untouched by this preset-specific page.
  if (preset === "asfashion") {
    return <AsFashionHome />;
  }
  return <LegacyHome branding={branding} preset={preset} />;
}

function LegacyHome({ branding, preset }) {
  const Hero = HERO_BY_PRESET[preset] || KalkiHero;
  const CategoryGrid = CATEGORY_BY_PRESET[preset] || KalkiCategoryGrid;
  // `resolveAppDisplayName`, not `brand_name`: every new tenant inherits the
  // platform default brand_name ("GetOwnApp Commerce", settings.py DEFAULTS)
  // until an admin edits Branding, whereas `mobile_app_name` falls back to the
  // tenant's own `name` server-side and is therefore always tenant-specific.
  const brandName = resolveAppDisplayName(branding);

  // Section visibility — defaults to ON for backwards compat.
  const vis = branding?.home_sections_visibility || {};
  // Platform-level Apple 5.2.3 kill-switch takes precedence over the
  // per-tenant `social` toggle. When `branding.reels_enabled === false`,
  // the social rail stays hidden even if the tenant left it visible.
  const reelsAllowed = branding?.reels_enabled !== false;
  const show = (key) => {
    if (key === "social" && !reelsAllowed) return false;
    return vis[key] !== false;
  };

  // Editable Featured / Brand Story content (tenant override > default).
  // `??` preserves explicit blanks ("") — admin can hide a field by
  // saving it empty without falling back to the placeholder copy.
  const f = branding?.home_featured || {};
  const featured = {
    overline:  f.overline  ?? FEATURED_DEFAULTS.overline,
    title:     f.title     ?? FEATURED_DEFAULTS.title,
    body:      f.body      ?? FEATURED_DEFAULTS.body,
    cta_label: f.cta_label ?? FEATURED_DEFAULTS.cta_label,
    cta_link:  f.cta_link  || FEATURED_DEFAULTS.cta_link,
    image_url: (f.image_url || "").trim() || resolveBrandImage(branding),
  };
  const bs = branding?.home_brand_story || {};
  const brandStory = {
    overline:  bs.overline  ?? `The ${brandName} Promise`,
    title:     bs.title     ?? "Genuine products, delivered fast.",
    body:      bs.body      ?? `${brandName} brings together thousands of verified sellers in one marketplace — so you get authentic products, transparent prices and a reliable delivery experience on everything from the latest smartphones to everyday kitchen essentials.`,
    cta_label: bs.cta_label ?? "Become a seller",
    cta_link:  bs.cta_link  || "/seller/onboard",
    image_url: (bs.image_url || "").trim() || resolveBrandImage(branding),
  };

  const [categories, setCategories] = useState([]);
  const [trending, setTrending] = useState([]);
  const [sections, setSections] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [c, t, s, b] = await Promise.all([
          api.get("/categories"),
          api.get("/products?limit=8&sort=new"),
          api.get("/homepage-sections").catch(() => ({ data: [] })),
          api.get("/brands?limit=16").catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        setCategories(c.data);
        setTrending(t.data.items);
        setSections(s.data || []);
        setBrands(b.data || []);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Could not load homepage content.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div data-testid="home-page" data-theme-preset={preset}>
      <Hero branding={branding} />
      {show("categories") && <CategoryGrid categories={categories} />}
      {show("campaigns") && <CampaignsRail />}
      {show("brands") && <BrandsRail brands={brands} />}
      {show("social") && <SocialRail />}
      {sections.map((s) => <HomeSection key={s.id} section={s} />)}

      {/* FEATURED — Editor's Pick / Mega Deals (tenant-editable) */}
      {show("featured") && (
        <section
          className="px-6 md:px-12 lg:px-24 py-20 bg-[hsl(var(--kb-surface-alt))]"
          data-testid="home-featured"
        >
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-5">
              <div className="overline mb-3">{featured.overline}</div>
              <h2 className="font-display text-4xl lg:text-6xl leading-tight mb-6">
                {featured.title}
              </h2>
              <p className="text-[hsl(var(--kb-muted))] leading-relaxed mb-8 whitespace-pre-line">
                {featured.body}
              </p>
              <Link
                to={featured.cta_link}
                className="kb-btn-primary inline-block"
                data-testid="featured-shop-btn"
              >
                {featured.cta_label}
              </Link>
            </div>
            {featured.image_url && (
              <div className="md:col-span-7 aspect-[4/5] md:aspect-[16/11] overflow-hidden">
                <img src={featured.image_url} alt="" className="w-full h-full object-cover" />
              </div>
            )}
          </div>
        </section>
      )}

      {show("trending") && (
        <TrendingGrid products={trending} preset={preset} loading={loading} error={error} />
      )}

      {/* BRAND STORY — Become a seller (tenant-editable) */}
      {show("brand_story") && (
        <section className="grid md:grid-cols-2 min-h-[70vh]" data-testid="home-brand-story">
          {brandStory.image_url && (
            <div className="relative aspect-square md:aspect-auto">
              <img src={brandStory.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
            </div>
          )}
          <div className="flex flex-col justify-center p-10 md:p-20 bg-[hsl(var(--kb-primary))] text-white">
            <div className="overline text-white/70 mb-4">{brandStory.overline}</div>
            <h2 className="font-display text-4xl lg:text-5xl mb-6 leading-tight">
              {brandStory.title}
            </h2>
            <p className="text-white/80 leading-relaxed mb-8 whitespace-pre-line">
              {brandStory.body}
            </p>
            <Link
              to={brandStory.cta_link}
              className="bg-white text-[hsl(var(--kb-primary))] px-8 py-4 uppercase text-sm tracking-wider font-medium inline-block w-max"
            >
              {brandStory.cta_label}
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
